import React, { useState, useMemo } from "react";
import { PackagePlus, Check, ArrowDownToLine, Pencil, Trash2, X, Plus } from "lucide-react";
import { useApp, formatDate } from "../context/AppContext";
import { Autocomplete } from "../components/Autocomplete";
import { ProdutoField, NovoProdutoButton } from "../components/ProdutoField";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";

const FornecimentoFamiliar = () => {
  const { residentesAtivos, produtos, fornecimentos, registrarFornecimento, editarFornecimento, excluirFornecimento, getProduto, getResidente, refresh, podeAcao } = useApp();
  const podeCriar = podeAcao("fornecimento", "criar");
  const podeEditar = podeAcao("fornecimento", "editar");
  const podeExcluir = podeAcao("fornecimento", "excluir");
  const { toast } = useToast();
  const [editId, setEditId] = useState(null);
  const [fRes, setFRes] = useState("todos");
  const [fProd, setFProd] = useState("todos");
  const [dIni, setDIni] = useState("");
  const [dFim, setDFim] = useState("");
  const [delAlvo, setDelAlvo] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [form, setForm] = useState({
    residenteId: "", data: new Date().toISOString().slice(0, 10), entreguePor: "",
  });
  const novoItem = () => ({ key: Math.random().toString(36).slice(2), produtoId: "", quantidade: "" });
  const [itens, setItens] = useState([novoItem()]);
  const [novosProdutos, setNovosProdutos] = useState([]);
  const addItem = () => setItens((xs) => [...xs, novoItem()]);
  const onProdutoCriado = (p) => {
    setNovosProdutos((xs) => [...xs, p]);
    setItens((xs) => {
      const vazio = xs.find((x) => !x.produtoId);
      if (vazio) return xs.map((x) => (x.key === vazio.key ? { ...x, produtoId: p.id } : x));
      return [...xs, { key: Math.random().toString(36).slice(2), produtoId: p.id, quantidade: "" }];
    });
  };
  const removeItem = (key) => setItens((xs) => (xs.length > 1 ? xs.filter((x) => x.key !== key) : xs));
  const setItem = (key, patch) => setItens((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const sortKey = (x) => x.criadoEm || (x.data ? `${x.data}T00:00:00` : "");
  const residentesFiltro = useMemo(() => {
    const ids = [...new Set(fornecimentos.map((f) => f.residenteId))];
    return ids.map((id) => getResidente(id)).filter(Boolean).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [fornecimentos]);
  const produtosFiltro = useMemo(() => {
    const ids = [...new Set(fornecimentos.map((f) => f.produtoId))];
    return ids.map((id) => getProduto(id)).filter(Boolean).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [fornecimentos]);
  const fornecimentosFiltrados = useMemo(() => (
    [...fornecimentos]
      .filter((f) => (fRes === "todos" || f.residenteId === fRes)
        && (fProd === "todos" || f.produtoId === fProd)
        && (!dIni || (f.data || "") >= dIni)
        && (!dFim || (f.data || "") <= dFim))
      .sort((a, b) => sortKey(b).localeCompare(sortKey(a)))
  ), [fornecimentos, fRes, fProd, dIni, dFim]);
  const temFiltro = fRes !== "todos" || fProd !== "todos" || dIni || dFim;
  const limparFiltros = () => { setFRes("todos"); setFProd("todos"); setDIni(""); setDFim(""); setPage(1); };
  const totalPaginas = Math.max(1, Math.ceil(fornecimentosFiltrados.length / pageSize));
  const pageSafe = Math.min(page, totalPaginas);
  const fornecimentosPagina = fornecimentosFiltrados.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const resetForm = () => { setEditId(null); setForm({ residenteId: "", data: new Date().toISOString().slice(0, 10), entreguePor: "" }); setItens([novoItem()]); };
  const iniciarEdicao = (f) => {
    setEditId(f.id);
    setForm({ residenteId: f.residenteId, data: f.data, entreguePor: f.entreguePor || "" });
    setItens([{ key: Math.random().toString(36).slice(2), produtoId: f.produtoId, quantidade: String(f.quantidade) }]);
  };
  const remover = (f) => setDelAlvo(f);
  const confirmarRemover = async () => {
    if (!delAlvo) return;
    await excluirFornecimento(delAlvo.id);
    toast({ title: "Fornecimento excluído" });
    if (editId === delAlvo.id) resetForm();
    setDelAlvo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.residenteId) { toast({ title: "Selecione o residente" }); return; }
    const res = getResidente(form.residenteId);
    if (editId) {
      const it = itens[0];
      if (!it.produtoId || !(Number(it.quantidade) > 0)) { toast({ title: "Informe a quantidade" }); return; }
      await editarFornecimento(editId, { quantidade: Number(it.quantidade), data: form.data, entreguePor: form.entreguePor });
      toast({ title: "Fornecimento atualizado" });
      resetForm();
      return;
    }
    const validos = itens.filter((it) => it.produtoId && Number(it.quantidade) > 0);
    if (validos.length === 0) { toast({ title: "Adicione ao menos um produto com quantidade" }); return; }
    let ok = 0; let falhou = 0;
    for (const it of validos) {
      try {
        await registrarFornecimento({ residenteId: form.residenteId, produtoId: it.produtoId, quantidade: Number(it.quantidade), data: form.data, entreguePor: form.entreguePor }, { skipRefresh: true });
        ok += 1;
      } catch (e) { falhou += 1; }
    }
    refresh();
    if (ok > 0) {
      toast({ title: `${ok} produto(s) registrado(s)${falhou ? ` · ${falhou} falharam` : ""}`, description: `Adicionados ao estoque de ${res?.nome}.` });
      setItens([novoItem()]);
    } else {
      toast({ title: "Não foi possível registrar", description: "Tente novamente em instantes." });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Fornecimento Familiar</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          Registre os produtos trazidos pela família. Eles entram no estoque pessoal do residente.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-slate-100 shadow-sm p-5 h-fit">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
            <PackagePlus className="w-4 h-4 text-blue-600" /> {editId ? "Editar Entrada" : "Nova Entrada"}
          </h2>
          {editId && (
            <div className="mb-3 flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700" data-testid="fornec-edit-banner">
              Editando · residente/produto fixos <button onClick={resetForm} className="text-amber-700 hover:text-amber-900 flex items-center gap-1" data-testid="fornec-cancelar-edicao"><X className="w-3.5 h-3.5" /> Cancelar</button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Residente</label>
              <Autocomplete
                testid="fornec-residente-busca"
                items={residentesAtivos.map((r) => ({ id: r.id, label: r.nome, sub: r.quarto ? `Q${r.quarto}` : "" }))}
                value={form.residenteId}
                onChange={(id) => setForm({ ...form, residenteId: id })}
                placeholder="Digite o nome do residente..."
              />
            </div>
            <div className="space-y-2.5">
              <label className="text-sm font-medium text-slate-600 block">{editId ? "Produto" : "Produtos"}</label>
              {itens.map((it, idx) => (
                <div key={it.key} className="rounded-lg border border-slate-100 p-3 space-y-2" data-testid={`fornec-item-${idx}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <ProdutoField testid={`fornec-produto-busca-${idx}`} value={it.produtoId} showNovo={false} extraItems={novosProdutos}
                        onChange={(id) => setItem(it.key, { produtoId: id })} placeholder="Digite o nome do produto..." />
                    </div>
                    {!editId && itens.length > 1 && (
                      <button type="button" onClick={() => removeItem(it.key)} className="text-red-500 hover:text-red-700 p-2 shrink-0" title="Remover" data-testid={`fornec-remover-item-${idx}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-slate-500 shrink-0">Quantidade:</label>
                    <input type="number" min="1" value={it.quantidade} placeholder="Qtd"
                      onChange={(e) => setItem(it.key, { quantidade: e.target.value })}
                      className="w-24 px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 text-sm" data-testid={`fornec-qtd-${idx}`} />
                  </div>
                </div>
              ))}
              {!editId && (
                <div className="flex gap-2">
                  <button type="button" onClick={addItem} className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-emerald-600 border border-dashed border-emerald-300 rounded-lg py-2 hover:bg-emerald-50" data-testid="fornec-add-item"><Plus className="w-4 h-4" /> Adicionar produto</button>
                  <NovoProdutoButton className="shrink-0" label="Novo produto" testid="fornec-novo-produto" onCreated={onProdutoCriado} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Data</label>
                <input type="date" value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm" data-testid="fornec-data" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Entregue por</label>
                <input value={form.entreguePor}
                  onChange={(e) => setForm({ ...form, entreguePor: e.target.value })}
                  placeholder="Nome do familiar"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm" />
              </div>
            </div>
            {(editId ? podeEditar : podeCriar) ? (
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 gap-2" data-testid="fornec-submit">
                <Check className="w-4 h-4" /> {editId ? "Salvar alteração" : "Registrar Entrada"}
              </Button>
            ) : (
              <p className="text-xs text-center text-slate-400" data-testid="fornec-sem-permissao">Você não tem permissão para {editId ? "alterar" : "registrar"} fornecimento.</p>
            )}
          </form>
        </div>

        {/* Histórico */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <ArrowDownToLine className="w-4 h-4 text-blue-600" /> Histórico de Fornecimentos
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-44">
                <Select value={fRes} onValueChange={(v) => { setFRes(v); setPage(1); }}>
                  <SelectTrigger data-testid="fornec-filtro-residente"><SelectValue placeholder="Residente" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="todos">Todos os residentes</SelectItem>
                    {residentesFiltro.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-44">
                <Select value={fProd} onValueChange={(v) => { setFProd(v); setPage(1); }}>
                  <SelectTrigger data-testid="fornec-filtro-produto"><SelectValue placeholder="Produto" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="todos">Todos os produtos</SelectItem>
                    {produtosFiltro.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500">De</label>
                <input type="date" value={dIni} onChange={(e) => { setDIni(e.target.value); setPage(1); }} data-testid="fornec-filtro-data-ini"
                  className="px-2 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 text-sm" />
                <label className="text-xs text-slate-500">até</label>
                <input type="date" value={dFim} onChange={(e) => { setDFim(e.target.value); setPage(1); }} data-testid="fornec-filtro-data-fim"
                  className="px-2 py-2 rounded-lg border border-slate-200 outline-none focus:border-blue-500 text-sm" />
              </div>
              {temFiltro && (
                <button onClick={limparFiltros} className="text-slate-400 hover:text-red-500 flex items-center gap-1 text-xs" data-testid="fornec-limpar-filtros"><X className="w-4 h-4" /> Limpar</button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-3 font-medium">Data</th>
                  <th className="px-5 py-3 font-medium">Residente</th>
                  <th className="px-5 py-3 font-medium">Produto</th>
                  <th className="px-5 py-3 font-medium">Qtd</th>
                  <th className="px-5 py-3 font-medium">Entregue por</th>
                  <th className="px-5 py-3 font-medium">Registrado por</th>
                  <th className="px-5 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {fornecimentosPagina.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">Nenhum fornecimento encontrado.</td></tr>
                )}
                {fornecimentosPagina.map((f) => {
                  const prod = getProduto(f.produtoId);
                  const res = getResidente(f.residenteId);
                  return (
                    <tr key={f.id} className="hover:bg-slate-50/60" data-testid={`fornec-row-${f.id}`}>
                      <td className="px-5 py-3.5 text-slate-500">{formatDate(f.data)}</td>
                      <td className="px-5 py-3.5 font-medium text-slate-700">{res?.nome}</td>
                      <td className="px-5 py-3.5 text-slate-600">{prod?.nome}</td>
                      <td className="px-5 py-3.5">
                        <span className="font-semibold text-blue-600">+{f.quantidade}</span> {prod?.unidade}
                      </td>
                      <td className="px-5 py-3.5 text-slate-500">{f.entreguePor}</td>
                      <td className="px-5 py-3.5 text-slate-500">{f.registradoPor || "—"}</td>
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        {podeEditar && <button onClick={() => iniciarEdicao(f)} className="text-amber-500 hover:text-amber-600 p-1.5" title="Editar" data-testid={`fornec-editar-${f.id}`}><Pencil className="w-4 h-4" /></button>}
                        {podeExcluir && <button onClick={() => remover(f)} className="text-red-500 hover:text-red-700 p-1.5" title="Excluir" data-testid={`fornec-excluir-${f.id}`}><Trash2 className="w-4 h-4" /></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 flex-wrap" data-testid="fornec-paginacao">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span>Itens por página:</span>
              <div className="w-20"><Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger data-testid="fornec-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>{[15, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select></div>
              <span className="text-slate-400">· {fornecimentosFiltrados.length} registro(s)</span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="fornec-pagina-anterior">Anterior</Button>
              <span className="text-sm text-slate-500">Página {pageSafe} de {totalPaginas}</span>
              <Button variant="outline" size="sm" disabled={pageSafe >= totalPaginas} onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))} data-testid="fornec-pagina-proxima">Próxima</Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!delAlvo} onOpenChange={(o) => { if (!o) setDelAlvo(null); }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="fornec-excluir-dialog">
          <DialogHeader><DialogTitle>Excluir fornecimento</DialogTitle></DialogHeader>
          {delAlvo && (
            <p className="text-sm text-slate-600">
              Excluir o fornecimento de <span className="font-semibold">{getProduto(delAlvo.produtoId)?.nome}</span> (+{delAlvo.quantidade} {getProduto(delAlvo.produtoId)?.unidade}) de <span className="font-semibold">{getResidente(delAlvo.residenteId)?.nome}</span>?
              <br /><br />
              A quantidade será retirada do estoque familiar do residente.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelAlvo(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 gap-2" onClick={confirmarRemover} data-testid="fornec-confirmar-excluir"><Trash2 className="w-4 h-4" /> Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FornecimentoFamiliar;
